import { create } from 'zustand';
import { getVisitorId, getVisitId } from '../lib/utils';

export type VisitorRegion = 'local' | 'overseas' | 'unknown';

interface GeoState {
  country: string | null;
  visitorRegion: VisitorRegion;
  loaded: boolean;
  load: () => Promise<void>;
}

// 訪問級快取（sessionStorage）：同一到訪只查一次／服務端只記一列 visit_logs；
// 快取帶 visit_id——30 分鐘休止後的新到訪會重新查詢（服務端補一列訪問日誌）。
const GEO_CACHE_KEY = 'x1493_geo';
// QA 除錯覆寫：localStorage 設 x1493_geo_override='HK' 或 'OS'（或任意 ISO 國家碼）模擬 IP 所在地
const GEO_OVERRIDE_KEY = 'x1493_geo_override';

let inflight: Promise<void> | null = null;

export const useGeoStore = create<GeoState>((set, get) => ({
  country: null,
  visitorRegion: 'unknown',
  loaded: false,

  load: async () => {
    if (get().loaded) return;
    if (inflight) return inflight;

    inflight = (async () => {
      try {
        if (typeof window === 'undefined') return;

        const override = window.localStorage.getItem(GEO_OVERRIDE_KEY);
        if (override) {
          const country = override.toUpperCase();
          set({ country, visitorRegion: country === 'HK' ? 'local' : 'overseas', loaded: true });
          return;
        }

        const visitId = getVisitId();
        const cached = window.sessionStorage.getItem(GEO_CACHE_KEY);
        if (cached) {
          const parsed = JSON.parse(cached) as { visitId: string; country: string | null; region: VisitorRegion };
          if (parsed.visitId === visitId) {
            set({ country: parsed.country, visitorRegion: parsed.region, loaded: true });
            return;
          }
        }

        const params = new URLSearchParams({ visitorId: getVisitorId(), visitId, sessionId: visitId });
        const res = await fetch(`/api/public/geo?${params.toString()}`);
        const json = (await res.json()) as { success?: boolean; data?: { country: string | null; region?: VisitorRegion } };
        const country = json?.data?.country ?? null;
        const region: VisitorRegion = json?.data?.region ?? (country ? (country === 'HK' ? 'local' : 'overseas') : 'unknown');
        set({ country, visitorRegion: region, loaded: true });
        try {
          window.sessionStorage.setItem(GEO_CACHE_KEY, JSON.stringify({ visitId, country, region }));
        } catch {
          // ignore sessionStorage errors
        }
      } catch {
        set({ loaded: true }); // geo 屬營銷提示，取不到＝unknown，靜默不影響任何功能
      } finally {
        inflight = null;
      }
    })();

    return inflight;
  },
}));
